import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories, mainSiteUrl } from "@/mocks/categories";
import { featuredArticles } from "@/mocks/seo-content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".dropdown-trigger") && !target.closest(".dropdown-menu")) {
        setDropdownOpen(false);
      }
      if (!target.closest(".guide-trigger") && !target.closest(".guide-menu")) {
        setGuideDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-background-50/75 backdrop-blur-sm"
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className="font-display text-xl md:text-2xl font-bold text-foreground-900 whitespace-nowrap"
          >
            pandabuy.surf
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 whitespace-nowrap transition-colors"
            >
              Home
            </Link>

            <div className="relative dropdown-trigger">
              <button
                onClick={() => {
                  setDropdownOpen(!dropdownOpen);
                  setGuideDropdownOpen(false);
                }}
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 whitespace-nowrap transition-colors"
              >
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="dropdown-menu absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-background-50 rounded-lg shadow-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.link}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="relative guide-trigger">
              <button
                onClick={() => {
                  setGuideDropdownOpen(!guideDropdownOpen);
                  setDropdownOpen(false);
                }}
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 whitespace-nowrap transition-colors"
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${guideDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {guideDropdownOpen && (
                <div className="guide-menu absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-background-50 rounded-lg shadow-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto">
                  {featuredArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      onClick={() => setGuideDropdownOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                    >
                      {article.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-full border border-foreground-300 text-foreground-700 hover:bg-foreground-50 whitespace-nowrap transition-all duration-300"
            >
              pandabuy sheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-5 py-2 text-sm font-semibold rounded-full bg-primary-500 text-white whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              buy it now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-8 h-8 flex items-center justify-center text-foreground-900"
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-6 py-4 max-h-[70vh] overflow-y-auto">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 py-3"
            >
              <i className="ri-home-4-line text-base w-5 h-5 flex items-center justify-center"></i>
              Home
            </Link>

            <div className="border-t border-background-100 my-1"></div>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between text-sm font-semibold text-foreground-900 py-3 w-full"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-base w-5 h-5 flex items-center justify-center"></i>
                Categories
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
            </button>
            {mobileCatOpen && (
              <div className="grid grid-cols-2 gap-1 ml-8 mb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.link}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 text-xs text-foreground-600 hover:text-primary-600 py-2 px-1 rounded-md hover:bg-primary-50 transition-colors"
                  >
                    <span className="w-2 h-2 rounded-full bg-primary-300 flex-shrink-0"></span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <div className="border-t border-background-100 my-1"></div>

            <button
              onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
              className="flex items-center justify-between text-sm font-semibold text-foreground-900 py-3 w-full"
            >
              <span className="flex items-center gap-3">
                <i className="ri-book-open-line text-base w-5 h-5 flex items-center justify-center"></i>
                Shopping Guide
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileGuideOpen ? "rotate-180" : ""}`}></i>
            </button>
            {mobileGuideOpen && (
              <div className="flex flex-col gap-0 ml-8 mb-2">
                {featuredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-xs text-foreground-600 hover:text-primary-600 py-2 px-1 rounded-md hover:bg-primary-50 transition-colors"
                  >
                    {article.title}
                  </Link>
                ))}
              </div>
            )}

            <div className="border-t border-background-100 mt-2 pt-3 flex flex-col gap-2">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 text-sm font-medium text-foreground-700 hover:text-primary-600 py-2"
              >
                <i className="ri-external-link-line text-base w-5 h-5 flex items-center justify-center"></i>
                pandabuy sheet
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-full bg-primary-500 text-white whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                <i className="ri-shopping-bag-line"></i>
                buy it now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}